import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Card, FormHelperText, Grid, Tooltip, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Custom3dotString from "../../../components/Custom3dotString";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../components/CustomTextField";
import SearchableSelect from "../../../components/Select/SearchableSelect";
import CustomSelect from "../../../components/Select/Select";
import {
  getSupplier,
  updateStockAdjust,
} from "../../../redux/reducers/clinicSlice";
import { createApprovalRequest } from "../../../redux/reducers/globalSettingsSlice";
import {
  createReturnOnBillingItems,
  updateReturnOnBillingItems,
} from "../../../redux/reducers/paymentSlice";
import { AppColors } from "../../../util/AppColors";
import { rolesForBilling, StockAdjustList } from "../../../util/arrayList";
import { RefundTypes, returnReasons } from "../../../util/dropList";
import { refreshMandateStocks } from "../../../util/fetchAndStoreMandate";
import {
  getRefundValues,
  getTaxAndSubTotalAndTotal,
  roundWithSignedDiff,
} from "../../../util/function";
import {
  buildTransferApprovalStep,
  getCountCorrectionApproveFlag,
  getCountCorrectionMatchedRule,
} from "../../../util/transferApproval";
import PaymentModeDropdown from "../../CommonComponents/PaymentModeDropdown";

const initAdjValues = {
  type: StockAdjustList?.[0],
  batchNumber: null,
  quantity: null,
  remarks: "",
  // supplier: null,
  product: null,
  // customer: null,
  // invoiceNumber: "",
  uom: null,
};
const initAdjErrs = {
  type: false,
  batchNumber: false,
  quantity: false,
  remarks: false,
  supplier: false,
  product: false,
  customer: false,
  invoiceNumber: false,
};
const initAdjHlps = {
  type: "",
  batchNumber: "",
  quantity: "",
  remarks: "",
  supplier: "",
  product: "",
  customer: "",
  invoiceNumber: "",
};
const nameExpan = {
  adjustId: "#",
  type: "Type",
  remarks: "Reference / Reason",
  locationName: "Location / Party",
  date: "Date",
  details: "Details",
};
const defObj = {
  stocks: [],
  vets: [],
  locations: [],
  bins: [],
  suppliers: [],
  clinic: {},
};

const StockAdjustment = ({ modVsble, onCloseMod = () => {} }) => {
  const dispatch = useDispatch();
  const [adjustValues, setAdjustValues] = useState(initAdjValues);
  const [adjustErrors, setAdjustErrors] = useState(initAdjErrs);
  const [adjustHelpers, setAdjustHelpers] = useState(initAdjHlps);
  //   const [userList, setUserList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [returnModVsble, setReturnModVsble] = useState(false);
  const [returnValue, setReturnValue] = useState({});
  const [selectedBill, setSelectedBill] = useState(null);
  const [listNotValid, setListNotValid] = useState(false);
  const [mandateSessionTick, setMandateSessionTick] = useState(0);
  const {
    stockAdjust,
    stocksByFilter,
    users,
    // locationList,
  } = useSelector((state) => state?.clinic);
  const approvalRules = useSelector(
    (state) => state?.globalSettings?.approvalRules,
  );
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const {
    stocks: clinicStocks,
    vets: clinicVets,
    locations,
    suppliers,
    clinic,
  } = useMemo(() => {
    if (!mandateDataRaw) return defObj;
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        stocks: Array.isArray(reqData?.stocks) ? reqData?.stocks : [],
        vets: Array.isArray(reqData?.vets) ? reqData?.vets : [],
        locations: Array.isArray(reqData?.locations) ? reqData?.locations : [],
        bins: Array.isArray(reqData?.bins) ? reqData.bins : [],
        suppliers: Array.isArray(reqData?.suppliers) ? reqData?.suppliers : [],
        clinic: reqData?.clinic ?? {},
      };
    } catch {
      return defObj;
    }
  }, [mandateDataRaw, mandateSessionTick]);

  useEffect(() => {
    if (!modVsble) return;
    refreshMandateStocks(dispatch).then(() => {
      setMandateSessionTick((tick) => tick + 1);
    });
  }, [modVsble]);

  useEffect(() => {
    if (!clinicStocks) return setProductList([]);
    const reqList = clinicStocks
      ?.filter((li) => li?.active === "Y")
      ?.map((prd) => {
        const name = prd?.tradeName ?? prd?.productName ?? "";
        return {
          name,
          value: name,
          stockId: prd?.stockId,
          productName: prd?.productName,
          tradeName: prd?.tradeName,
        };
      });
    setProductList(reqList);
  }, [clinicStocks]);

  const normalizeProductValue = (value) => {
    if (!value) return null;
    const stockId = value?.stockId ?? value?.value;
    const fromList = productList?.find(
      (prd) => String(prd?.stockId) === String(stockId),
    );
    if (fromList) return fromList;
    const name = value?.name ?? value?.tradeName ?? value?.productName ?? "";
    return {
      name,
      value: name,
      stockId: value?.stockId ?? stockId ?? null,
      productName: value?.productName,
      tradeName: value?.tradeName,
    };
  };

  const getAdjustFieldValue = (name, value) => {
    if (typeof value !== "object" || value === null) return value ?? "";
    if (name === "product") {
      return value?.name ?? value?.value ?? "";
    }
    return value?.value ?? "";
  };

  //   useEffect(() => {
  //     if (users?.length === 0 || !users) return setUserList([]);
  //     const reqList = users
  //       ?.filter((usr) => usr?.name?.length > 0)
  //       ?.map((sup) => ({ name: sup?.name, value: sup?.name, userId: sup?.id }))
  //       ?.sort((a, b) => a?.name?.localeCompare(b?.name));
  //     setUserList(reqList);
  //   }, [users]);

  const getAvailableBatchQty = (batch, stockId) => {
    const fromOption = Number(batch?.quantity);
    if (Number.isFinite(fromOption)) return fromOption;
    if (!batch) return null;
    const stock = (clinicStocks ?? []).find(
      (stc) => String(stc?.stockId) === String(stockId),
    );
    const raw = (stock?.stockBatch ?? stock?.batches ?? []).find((bat) =>
      batch?.batchId != null
        ? String(bat?.batchId) === String(batch.batchId)
        : String(bat?.batchNo ?? "") === String(batch?.value ?? ""),
    );
    const qty = Number(raw?.quantity);
    return Number.isFinite(qty) ? qty : null;
  };

  const getNegativeQtyLimitError = (quantity, batch, stockId) => {
    if (quantity === "" || quantity == null || !batch) return "";
    const enteredQty = Number(quantity);
    if (!Number.isFinite(enteredQty) || enteredQty >= 0) return "";
    const availableQty = getAvailableBatchQty(batch, stockId);
    if (availableQty == null) return "";
    if (Math.abs(enteredQty) > availableQty + 0.0001) {
      const shown = Math.max(0, Math.round(availableQty * 100) / 100);
      return `Quantity cannot exceed available quantity (${shown})`;
    }
    return "";
  };

  const handleChangeAdjustValue = (name, value) => {
    let normalizedValue = value;
    if (name === "product") {
      normalizedValue = normalizeProductValue(value);
    }
    const reqValue = getAdjustFieldValue(name, normalizedValue);
    const reqObj = { ...adjustValues, [name]: normalizedValue };
    // if (name === "type" && value?.value === "Return on Purchase") {
    //   dispatch(getSupplier());
    // }
    if (name === "supplierSearch" && value?.length > 2) {
      dispatch(getSupplier(`?search=${value}&itemSize=50&page=1`));
    }
    if (name === "product") {
      reqObj.batchNumber = null;
      reqObj.uom = null;
    }
    if (name === "batchNumber" && reqObj?.batchNumber?.value) {
      reqObj.uom = {
        name: reqObj?.batchNumber?.qtyMapName ?? "",
        value: reqObj?.batchNumber?.qtyMapName ?? "",
        uomMappingId: reqObj?.batchNumber?.qtyMapId ?? null,
      };
      // getUOMListByStockId(adjustValues?.product?.stockId);
    }
    const qtyRaw = name === "quantity" ? normalizedValue : reqObj?.quantity;
    const selectedBatch =
      name === "batchNumber" ? normalizedValue : reqObj?.batchNumber;
    const negativeQtyHelp =
      name === "quantity" || name === "batchNumber"
        ? getNegativeQtyLimitError(
            qtyRaw,
            selectedBatch,
            reqObj?.product?.stockId,
          )
        : "";
    const quantityFieldError = negativeQtyHelp
      ? true
      : name === "quantity"
        ? String(qtyRaw ?? "")?.length === 0
        : false;
    const quantityFieldHelp = negativeQtyHelp
      ? negativeQtyHelp
      : name === "quantity" && String(qtyRaw ?? "")?.length === 0
        ? "This field is Required"
        : "";
    setAdjustValues(reqObj);
    setAdjustErrors({
      ...adjustErrors,
      [name]:
        name === "quantity"
          ? quantityFieldError
          : String(reqValue ?? "")?.length > 0
            ? false
            : adjustValues?.type?.value !== "Other Adjustments" &&
                name === "remarks"
              ? false
              : name === "invoiceNumber"
                ? false
                : true,
      ...(name === "batchNumber" ? { quantity: quantityFieldError } : {}),
    });
    setAdjustHelpers({
      ...adjustHelpers,
      [name]:
        name === "quantity"
          ? quantityFieldHelp
          : String(reqValue ?? "")?.length > 0
            ? ""
            : adjustValues?.type?.value !== "Other Adjustments" &&
                name === "remarks"
              ? ""
              : name === "invoiceNumber"
                ? ""
                : `This field is Required`,
      ...(name === "batchNumber" ? { quantity: quantityFieldHelp } : {}),
    });
  };

  const getSupplierList = () => {
    const reqList = (suppliers ?? [])
      ?.map((sup) => ({
        name: sup?.supplierName,
        value: sup?.supplierName,
        supplierId: sup?.supplierId,
      }))
      ?.sort((a, b) => a?.name?.localeCompare(b?.name));
    return reqList;
  };

  const getBatchNumbersByProduct = (stkId = null) => {
    if (!stkId) return [];
    const reqPrd = clinicStocks?.find((stc) => stc?.stockId === stkId);
    if (reqPrd) {
      const reqList = (reqPrd?.stockBatch ?? reqPrd?.batches)
        ?.filter((bat) => bat?.createdBy === clinic?.id && bat?.approved)
        ?.map((bat) => {
          const reqLocation = locations?.find(
            (loc) => loc?.id === bat?.locationId,
          );
          return {
            name: bat?.batchNo,
            value: bat?.batchNo,
            subunits: bat?.subunits,
            quantity: bat?.quantity,
            locationId: bat?.locationId,
            locationName: reqLocation?.locationName ?? "",
            batchId: bat?.batchId,
            qtyMapId: bat?.qtyMapId ?? null,
            qtyMapName: bat?.qtyMapName ?? null,
            binName: bat?.binName ?? "",
          };
        });
      return reqList;
    }
    return [];
  };

  const handleValidation = () => {
    const errList = {};
    const hlpList = {};
    Object.keys(adjustValues).forEach(function (key) {
      const reqValue =
        typeof adjustValues[key] === "object"
          ? (adjustValues[key]?.value ?? null)
          : adjustValues[key];
      if (
        key === "quantity" &&
        adjustValues?.type?.value !== "Other Adjustments" &&
        Number(reqValue) < 1
      ) {
        errList[key] = true;
        hlpList[key] = "Quantity must be Positive number";
        return;
      }
      if (key === "quantity") {
        const negativeQtyHelp = getNegativeQtyLimitError(
          reqValue,
          adjustValues?.batchNumber,
          adjustValues?.product?.stockId,
        );
        if (negativeQtyHelp) {
          errList[key] = true;
          hlpList[key] = negativeQtyHelp;
          return;
        }
      }
      if (reqValue?.length === 0 || reqValue === null) {
        if (key === "invoiceNumber" || key === "uom") {
          errList[key] = false;
          hlpList[key] = "";
          return;
        }
        if (
          key === "remarks" &&
          adjustValues?.type?.value !== "Other Adjustments"
        ) {
          errList[key] = false;
          hlpList[key] = "";
          return;
        }
        // if (
        //   key === "supplier" &&
        //   adjustValues?.type?.value !== "Return on Purchase"
        // ) {
        //   errList[key] = false;
        //   hlpList[key] = "";
        //   return;
        // }
        // if (
        //   key === "customer" &&
        //   adjustValues?.type?.value !== "Return on Sales"
        // ) {
        //   errList[key] = false;
        //   hlpList[key] = "";
        //   return;
        // }
        errList[key] = true;
        hlpList[key] = `${nameExpan[key]} is Required`;
      }
    });
    setAdjustHelpers({ ...adjustHelpers, ...hlpList });
    setAdjustErrors({ ...adjustErrors, ...errList });
    return Object.values(errList).some((item) => item);
  };

  const handleStockAdjust = () => {
    if (!adjustValues?.type?.value) {
      setAdjustErrors({ ...adjustErrors, type: true });
      setAdjustHelpers({ ...adjustHelpers, type: "This field is Required" });
      return;
    }
    if (adjustValues?.type?.value === "Other Adjustments") {
      const validation = handleValidation();
      if (validation) return;
    }
    if (adjustValues?.type?.value === "Return on Purchase") {
      if (!adjustValues?.supplier?.value || !adjustValues?.invoiceNumber) {
        setAdjustErrors({
          ...adjustErrors,
          supplier: !adjustValues?.supplier?.value,
          invoiceNumber: !adjustValues?.invoiceNumber,
        });
        setAdjustHelpers({
          ...adjustHelpers,
          supplier: !adjustValues?.supplier?.value
            ? "This field is Required"
            : "",
          invoiceNumber: !adjustValues?.invoiceNumber
            ? "This field is Required"
            : "",
        });
        return;
      }
    }
    if (adjustValues?.type?.value === "Other Adjustments") {
      const approveFlag = getCountCorrectionApproveFlag(approvalRules);
      const data = {
        type: adjustValues?.type?.value,
        quantity:
          adjustValues?.type?.value === "Return on Purchase"
            ? Number(adjustValues?.quantity) * -1
            : Number(adjustValues?.quantity),
        batchNumber: adjustValues?.batchNumber?.value,
        productName: adjustValues?.product?.value,
        remarks: adjustValues?.remarks,
        batchId: adjustValues?.batchNumber?.batchId ?? null,
        stockId: adjustValues?.product?.stockId ?? null,
        approve: approveFlag,
        rejectReason: "",
        status: approveFlag === "Y" ? "completed" : "pending",
      };
      if (adjustValues?.type?.value !== "Other Adjustments") {
        data.invoiceNumber = adjustValues?.invoiceNumber;
      }
      if (adjustValues?.type?.value === "Other Adjustments") {
        data.uomName = adjustValues?.uom?.name ?? "";
        data.uomId = adjustValues?.uom?.uomMappingId ?? null;
      }
      if (adjustValues?.type?.value === "Return on Purchase") {
        data.supplierName = adjustValues?.supplier?.name;
        data.supplierId = adjustValues?.supplier?.supplierId;
      }
      if (adjustValues?.type?.value === "Return on Sales") {
        data.customerName = adjustValues?.customer?.name;
        data.userId = adjustValues?.customer?.userId;
      }
      dispatch(updateStockAdjust(data)).then(async (res) => {
        if (!res?.payload) return;

        const createdAdjust =
          res.payload?.adjustId != null
            ? res.payload
            : (res.payload?.data ?? res.payload);
        const adjustId = createdAdjust?.adjustId ?? null;

        if (approveFlag === "N" && adjustId) {
          const matchedRule = getCountCorrectionMatchedRule(approvalRules);
          const detail = createdAdjust?.stockAdjustDetails?.[0];
          const productName =
            adjustValues?.product?.value ||
            detail?.productName ||
            "Stock Adjustment";
          const quantity = Number(
            adjustValues?.quantity ?? detail?.quantity ?? 0,
          );
          await dispatch(
            createApprovalRequest({
              priority: "HIGH",
              requestType: "COUNT_CORRECTION",
              summary: `Stock Adjustment`,
              approvalStep: buildTransferApprovalStep(matchedRule),
              quantity,
              amount: quantity,
              referenceType: "STOCK_ADJUSTMENT",
              referenceId: adjustId,
              referenceNo: String(productName),
              autoApproval: false,
              adjustId,
              noOfItems: quantity,
              requestedBy: (() => {
                try {
                  const user = JSON.parse(localStorage.getItem("user") || "{}");
                  return user?.id || user?.doctorId || null;
                } catch {
                  return null;
                }
              })(),
            }),
          );
        }

        onCloseMod("apiSuccess");
      });
    }

    if (adjustValues?.type?.value === "Return on Purchase") {
      const reqBill = (suppliers ?? [])?.find(
        (pay) => pay?.supplierId === adjustValues?.invoiceNumber?.supplierId,
      );
      const reqSupDet = reqBill?.supplierDetails?.find(
        (suDe) => suDe?.detailId === adjustValues?.invoiceNumber?.detailId,
      );
      const reqTableList = reqSupDet?.supplierInvoiceDetails?.map((sil) => {
        const mrpPrice =
          Number(sil?.itemTotal ?? 0) / Number(sil?.quantity ?? 0);
        // const tax = Number(sil?.taxes ?? 0) / Number(sil?.quantity ?? 0);
        return {
          ...sil,
          total: sil?.itemTotal,
          returnQuantity: 0,
          name: sil?.tradeName,
          mrpPrice,
          tax: sil?.taxes ?? 0,
          price: mrpPrice ?? 0,
        };
      });
      const returnObj = {
        refundMethod: {
          name: "Advance Adjustment",
          value: "Advance Adjustment",
        },
        remarks: reqBill?.remarks ?? "",
        returnReason: returnReasons?.[0],
        personInCharge: null,
        invoiceNumber: adjustValues?.invoiceNumber,
        list: reqTableList,
        type: adjustValues?.type?.value,
      };
      setSelectedBill({
        ...reqBill,
        createdDate: reqSupDet?.billDate,
        invoiceNumbers: reqSupDet?.invoiceNumber,
      });
      setReturnValue(returnObj);
      setReturnModVsble(true);
      return;
    }
  };

  const getFilteredSupplierList = () => {
    const reqList = suppliers?.filter((usr) => {
      if (
        usr?.supplierName
          ?.toLowerCase()
          ?.includes((stockAdjust?.supplierSearch ?? "")?.toLowerCase())
      ) {
        return usr;
      }
    });
    return reqList ?? [];
  };

  const getSupInvoiceList = () => {
    if (!adjustValues?.supplier?.value) return [];
    return (suppliers ?? [])
      ?.find((sup) => sup?.supplierId === adjustValues?.supplier?.value)
      ?.supplierDetails?.map((det) => {
        const reqList = (det?.subpplierInvoiceDetails ?? [])?.map((sd) => ({
          tax: sd?.taxes ?? 0,
          total: sd?.itemTotal ?? 0,
        }));
        const reqAmtData = roundWithSignedDiff(
          getTaxAndSubTotalAndTotal(reqList)?.total,
        );
        return {
          name: `${det?.invoiceNumber ?? ""}, ₹${reqAmtData?.rounded}, ${moment(new Date(det?.billDate)).format("DD MMM YYYY")}`,
          value: det?.invoiceNumber ?? "",
          detailId: det?.detailId,
          supplierId: adjustValues?.supplier?.value,
        };
      });
  };

  const handleSelectedSupplier = (sup) => {
    setAdjustValues({
      ...adjustValues,
      supplier: { name: sup?.supplierName, value: sup?.supplierId },
      supplierSearch: "",
    });
    setAdjustErrors({ ...adjustErrors, supplier: false });
    setAdjustHelpers({ ...adjustHelpers, supplier: "" });
  };

  const handleCloseUpdateMod = () => {
    setReturnModVsble(false);
    setSelectedBill(null);
    setReturnValue({});
  };

  const handleChange = (name, value) => {
    setReturnValue({ ...returnValue, [name]: value });
  };

  const handleChangeTblValue = (name, value, ind) => {
    const reqList = returnValue?.list?.map((li, i) => {
      if (i === ind) {
        const reqObj = { ...li, [name]: value };
        if (name === "returnQuantity") {
          reqObj.returnQuantityErr =
            Number(li?.quantity) < Number(value) ? "Maximum limit reached" : "";
        }
        return reqObj;
      }
      return li;
    });
    if (listNotValid) {
      if (reqList?.some((li) => Number(li?.returnQuantity) > 0)) {
        setListNotValid(false);
      }
    }
    const reqValue = { ...returnValue, list: reqList };
    setReturnValue(reqValue);
  };

  const getPersonInChargeList = () => {
    return (clinicVets ?? [])
      ?.filter((li) => rolesForBilling.includes(li?.userRole))
      ?.map((li) => ({ name: li?.name, value: li?.doctorId }));
  };

  const submitReturn = (status = "") => {
    const returnList = returnValue?.list;
    // ?.filter((li) =>
    //   // Number(li?.returnQuantity) > 0 &&
    //   activeTab === "Purchase" ? li?.batchNumber?.value : true,
    // );
    if (returnList?.length === 0 || !returnList) return setListNotValid(true);
    if (returnValue?.type === "Return on Purchase") {
      const paymentDetails = returnList?.map((li, i) => {
        const unitPrice = isNaN(
          Number(Number(li?.total ?? li?.itemTotal) / Number(li?.quantity)),
        )
          ? 0
          : Number(Number(li?.total ?? li?.itemTotal) / Number(li?.quantity));
        const unitTax = isNaN(Number(Number(li?.tax) / Number(li?.quantity)))
          ? 0
          : Number(Number(li?.tax) / Number(li?.quantity));
        const reqbj = {
          unitPrice,
          unitTax,
          quantity: li?.quantity ?? 0,
          tax: Number(Number(unitTax ?? 0) * Number(li?.returnQuantity ?? 0)),
          price: li?.price,
          total: Number(
            Number(unitPrice ?? 0) * Number(li?.returnQuantity ?? 0),
          ),
          returnQuantity: Number(li?.returnQuantity),
          name: li?.name,
          // paymentDetailId: li?.paymentDetailId ?? null,
          stockId: li?.stockId ?? null,
          batchId: li?.batchId ?? null,
          paymentDetailId: null,
          returnDetailId: li?.paymentDetailId ?? null,
        };
        if (li?.uomName) reqbj.uomName = li?.uomName;
        if (li?.uomId) reqbj.uomId = li?.uomId;
        if (li?.serviceId) reqbj.serviceId = li?.serviceId;
        if (li?.stockId) reqbj.stockId = li?.stockId;
        if (li?.batchId) reqbj.batchId = li?.batchId;
        if (li?.paymentDetailId) reqbj.paymentDetailId = li?.paymentDetailId;
        return reqbj;
      });

      // const paymentDetails = returnList?.map((li, i) => {
      //   let reqbj = {
      //     returnQuantity: Number(li?.returnQuantity),
      //     name: li?.name,
      //   };
      //   if (returnValue?.type !== "Return on Purchase") {
      //     reqbj.tax = li?.tax;
      //     reqbj.price = li?.price;
      //     reqbj.total = li?.total;
      //   }
      //   if (returnValue?.type === "Return on Purchase") {
      //     reqbj = {
      //       ...reqbj,
      //       ...(li ?? {}),
      //       returnQuantity: Number(li?.returnQuantity),
      //     };
      //     reqbj.tax = Number(li?.tax ?? 0 / li?.quantity ?? 0)?.toFixed(2);
      //     reqbj.price = li?.mrpPrice;
      //     reqbj.total = Number(
      //       Number(li?.mrpPrice ?? 0) * Number(li?.returnQuantity ?? 0),
      //     );
      //   }
      //   if (li?.serviceId) reqbj.serviceId = li?.serviceId;
      //   if (li?.stockId) reqbj.stockId = li?.stockId;
      //   if (li?.batchId) reqbj.batchId = li?.batchId;
      //   if (li.paymentDetailId) reqbj.paymentDetailId = li.paymentDetailId;
      //   return reqbj;
      // });
      const sumPaymentDetailsBy = (key) =>
        paymentDetails?.reduce((acc, obj) => acc + Number(obj?.[key] ?? 0), 0);
      const amountPaid = Number(
        selectedBill?.tab === "petShop"
          ? (selectedBill?.petShopAmtPaid ?? 0)
          : selectedBill?.tab === "pharmacy"
            ? (selectedBill?.pharmacyAmtPaid ?? 0)
            : (selectedBill?.amtPaid ?? 0),
      );
      const remainingItemsValue = (returnValue?.list ?? [])?.reduce(
        (acc, li) => {
          const remainingQty = Math.max(
            0,
            Number(li?.quantity ?? 0) - Number(li?.returnQuantity ?? 0),
          );
          const unitPrice = isNaN(
            Number(Number(li?.total) / Number(li?.quantity)),
          )
            ? 0
            : Number(Number(li?.total) / Number(li?.quantity));
          return acc + Number(unitPrice) * remainingQty;
        },
        0,
      );
      const refundAmount = Math.max(
        0,
        amountPaid - Number(remainingItemsValue ?? 0),
      );

      const data = {
        paymentMode: amountPaid > 0 ? returnValue?.refundMethod?.value : null,
        personInCharge: returnValue?.personInCharge?.value ?? null,
        returnReason: returnValue?.returnReason?.value,
        paymentDetails,
        creditSave: status,
        remarks: returnValue?.remarks ?? "",
        invoiceNumbers: returnValue?.invoiceNumber?.value ?? "",
        refundAmount: -Number(refundAmount),
        totalAmt: -Number(sumPaymentDetailsBy("total") ?? 0),
        totalTax: -Number(sumPaymentDetailsBy("tax") ?? 0),
      };

      // const data = {
      //   paymentDetails,
      //   creditSave: status,
      //   remarks: returnValue?.remarks ?? "",
      //   paymentMode: returnValue?.refundMethod?.value,
      //   personInCharge: returnValue?.personInCharge?.value ?? null,
      //   returnReason: returnValue?.returnReason?.value,
      //   invoiceNumbers: returnValue?.invoiceNumber?.value,
      //   totalAmt: Number(sumPaymentDetailsBy("total") ?? 0),
      //   totalTax: Number(sumPaymentDetailsBy("tax") ?? 0),
      // };
      if (returnValue?.type !== "Return on Purchase") data.type = "sales";
      if (returnValue?.type === "Return on Purchase") data.type = "purchase";
      dispatch(
        selectedBill?.returnId
          ? updateReturnOnBillingItems({
              data,
              id: selectedBill?.returnId,
            })
          : createReturnOnBillingItems({
              data,
              id:
                returnValue?.type === "Return on Purchase"
                  ? returnValue?.invoiceNumber?.detailId
                  : (selectedBill?.paymentId ??
                    returnValue?.invoiceNumber?.paymentId ??
                    returnValue?.invoiceNumber?.detailId),
            }),
      ).then((res) => {
        if (res?.payload) {
          onCloseMod("apiSuccess");
          handleCloseUpdateMod();
        }
      });
      // dispatch(
      //   updateReturnOnBillingItems({
      //     data,
      //     id:
      //       returnValue?.type === "Return on Purchase"
      //         ? returnValue?.invoiceNumber?.detailId
      //         : (selectedBill?.paymentId ??
      //           returnValue?.invoiceNumber?.paymentId ??
      //           returnValue?.invoiceNumber?.detailId),
      //   }),
      // ).then((res) => {
      //   if (res?.payload) {
      //     onCloseMod("apiSuccess");
      //     handleCloseUpdateMod();
      //   }
      // });
    }
  };

  return (
    <>
      <CustomModal
        open={modVsble}
        onClose={() => onCloseMod("close")}
        header="Stock Adjustment"
        modalWidth={30}
        rightModal
      >
        <div className="scroll-90vh">
          <Grid container spacing={2} className="ph20">
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="txt-semi-bold fs14 light-grey">
                Type <span className="red-color">*</span>
              </div>
              <CustomSelect
                list={StockAdjustList}
                handleChange={(e, value) =>
                  handleChangeAdjustValue("type", value)
                }
                value={adjustValues?.type}
                helperText={adjustHelpers?.type}
                error={adjustErrors?.type}
                search
              />
            </Grid>
            {adjustValues?.type?.value === "Return on Purchase" ? (
              <>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <CustomTextField
                    fullWidth
                    search
                    value={adjustValues?.supplierSearch}
                    placeholder="Search Supplier"
                    handleChange={(e) =>
                      handleChangeAdjustValue(
                        "supplierSearch",
                        e?.target?.value,
                      )
                    }
                    // backgroundColor={AppColors.gray6}
                  />
                </Grid>
                {adjustValues?.supplierSearch?.length > 2 && (
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    {getFilteredSupplierList()?.length > 0 &&
                      getFilteredSupplierList()?.map((sup) => {
                        return (
                          <Grid
                            container
                            spacing={1}
                            key={sup?.supplierId}
                            className="cursor"
                            onClick={() => handleSelectedSupplier(sup)}
                          >
                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                              <Grid container spacing={1}>
                                <Grid item xs={12}>
                                  <Card
                                    sx={{
                                      borderRadius: 1,
                                      padding: 2,
                                      borderTopColor: AppColors.appPrimary,
                                    }}
                                    className="CustomCard-back-appointment"
                                  >
                                    <div className="maint">
                                      <div className="flex-row">
                                        <Grid item>
                                          <div className="flex-row">
                                            <div className="flex-start">
                                              <div className="flex-column">
                                                <div className="flex-row">
                                                  <Typography className="mb10 font-bold fs14 capitalize">
                                                    {sup?.supplierName}
                                                  </Typography>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </Grid>
                                      </div>
                                    </div>
                                  </Card>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        );
                      })}
                  </Grid>
                )}
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <div className="txt-semi-bold fs14 light-grey">
                    Supplier <span className="red-color">*</span>
                  </div>
                  <CustomSelect
                    list={[]}
                    value={adjustValues?.supplier ?? null}
                    helperText={adjustHelpers?.supplier}
                    error={adjustErrors?.supplier}
                    search
                    disabled
                  />
                </Grid>

                {adjustValues?.supplier?.value ? (
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <div className="txt-semi-bold fs14 light-grey">
                      Invoice Number <span className="red-color">*</span>
                    </div>
                    <CustomSelect
                      list={getSupInvoiceList()}
                      handleChange={(e, value) =>
                        handleChangeAdjustValue("invoiceNumber", value)
                      }
                      value={adjustValues?.invoiceNumber}
                      helperText={adjustHelpers?.invoiceNumber}
                      error={adjustErrors?.invoiceNumber}
                      search
                    />
                  </Grid>
                ) : null}
              </>
            ) : null}
            {/* {adjustValues?.type?.value === "Return on Purchase" ? (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="txt-semi-bold fs14 light-grey">
                  Supplier <span className="red-color">*</span>
                </div>
                <CustomSelect
                  list={getSupplierList()}
                  handleChange={(e, value) =>
                    handleChangeAdjustValue("supplier", value)
                  }
                  value={adjustValues?.supplier}
                  helperText={adjustHelpers?.supplier}
                  error={adjustErrors?.supplier}
                  search
                />
              </Grid>
            ) : null} */}
            {/* {adjustValues?.type?.value === "Return on Sales" ? (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="txt-semi-bold fs14 light-grey">
                  Customer <span className="red-color">*</span>
                </div>
                <CustomSelect
                  list={userList}
                  handleChange={(e, value) =>
                    handleChangeAdjustValue("customer", value)
                  }
                  value={adjustValues?.customer}
                  helperText={adjustHelpers?.customer}
                  error={adjustErrors?.customer}
                  search
                />
              </Grid>
            ) : null} */}
            {adjustValues?.type?.value !== "Return on Purchase" ? (
              <>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <div className="txt-semi-bold fs14 light-grey">
                    Product <span className="red-color">*</span>
                  </div>
                  <SearchableSelect
                    list={productList}
                    value={adjustValues?.product ?? null}
                    handleChange={(e, value) =>
                      handleChangeAdjustValue("product", value)
                    }
                    maxFilteredOptions={100}
                    error={adjustErrors?.product}
                    helperText={adjustHelpers?.product}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <div className="txt-semi-bold fs14 light-grey">
                    Batch Number <span className="red-color">*</span>
                  </div>
                  <CustomSelect
                    list={getBatchNumbersByProduct(
                      adjustValues?.product?.stockId,
                    )}
                    handleChange={(e, value) =>
                      handleChangeAdjustValue("batchNumber", value)
                    }
                    value={adjustValues?.batchNumber}
                    helperText={adjustHelpers?.batchNumber}
                    error={adjustErrors?.batchNumber}
                    search
                    isBatchNumber
                  />
                  {/* <CustomSelect
                list={getBatchNumbersByProduct(adjustValues?.product?.value)}
                handleChange={(e, value) =>
                  handleChangeAdjustValue("batchNumber", value)
                }
                value={adjustValues?.batchNumber}
                helperText={adjustHelpers?.batchNumber}
                error={adjustErrors?.batchNumber}
                search
              /> */}
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                  <div className="txt-semi-bold fs14 light-grey">
                    Quantity <span className="red-color">*</span>
                    {adjustValues?.type?.value === "Other Adjustments" ? (
                      <Tooltip
                        title="Enter a positive value to increase 
                               the quantity or enter a negative 
                               value to decrease the quantity"
                        className="ml5"
                      >
                        <InfoOutlinedIcon
                          sx={{ width: 15, height: 15, color: AppColors.red }}
                        />
                      </Tooltip>
                    ) : null}
                  </div>

                  <CustomTextField
                    handleChange={(e) =>
                      handleChangeAdjustValue("quantity", e?.target?.value)
                    }
                    value={adjustValues?.quantity}
                    helperText={adjustHelpers?.quantity}
                    error={adjustErrors?.quantity}
                    fullWidth
                    type="number"
                  />
                </Grid>
                {adjustValues?.type?.value === "Other Adjustments" ? (
                  <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                    <div className="txt-semi-bold fs14 light-grey">UOM</div>
                    <CustomSelect
                      list={[adjustValues?.uom ?? {}]}
                      value={adjustValues?.uom}
                      search
                      disabled
                    />
                  </Grid>
                ) : null}
                {/* {adjustValues?.type?.value !== "Other Adjustments" ? (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="txt-semi-bold fs14 light-grey">
                  Invoice Number
                </div>
                <CustomTextField
                  handleChange={(e) =>
                    handleChangeAdjustValue("invoiceNumber", e?.target?.value)
                  }
                  value={adjustValues?.invoiceNumber}
                  helperText={adjustHelpers?.invoiceNumber}
                  error={adjustErrors?.invoiceNumber}
                  fullWidth
                />
              </Grid>
            ) : null} */}
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <div className="txt-semi-bold fs14 light-grey">
                    Remarks
                    {adjustValues?.type?.value === "Other Adjustments" ? (
                      <span className="red-color"> *</span>
                    ) : null}
                  </div>
                  <CustomTextField
                    handleChange={(e) =>
                      handleChangeAdjustValue("remarks", e?.target?.value)
                    }
                    value={adjustValues?.remarks}
                    helperText={adjustHelpers?.remarks}
                    error={adjustErrors?.remarks}
                    fullWidth
                    rows={3}
                    multiline
                    required={adjustValues?.type?.value === "Other Adjustments"}
                  />
                </Grid>
              </>
            ) : null}

            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="flex1-end mt10">
                <div>
                  <CustomButton
                    text="Submit"
                    smallBtn
                    onClick={handleStockAdjust}
                  />
                </div>
              </div>
            </Grid>
          </Grid>
        </div>
      </CustomModal>

      <CustomModal
        open={returnModVsble}
        onClose={handleCloseUpdateMod}
        header={returnValue?.type}
        modal
        modalWidth={90}
      >
        <div className="scroll-80vh">
          <div className="return-back">
            <div className="flex-row-ali-cen ph40 mv15">
              <div
                className={
                  returnValue?.type === "Return on Sales" ? "w20Per" : "w25Per"
                }
              >
                <Typography className="gray29 fs12 font-medium">
                  {returnValue?.type === "Return on Purchase"
                    ? "Supplier"
                    : "Pet"}{" "}
                  Name
                </Typography>
                <Typography className="fs14 black22 txt-semi-bold">
                  {(returnValue?.type === "Return on Purchase"
                    ? selectedBill?.supplierName
                    : selectedBill?.petName) ?? ""}
                </Typography>
              </div>
              <div
                className={
                  returnValue?.type === "Return on Sales" ? "w20Per" : "w25Per"
                }
              >
                <Typography className="gray29 fs12 font-medium">
                  {returnValue?.type === "Return on Purchase"
                    ? "Supplier GST No."
                    : "Parent Name"}
                </Typography>
                <Typography className="fs14 black22 txt-semi-bold">
                  {(returnValue?.type === "Return on Purchase"
                    ? (selectedBill?.gstNumber ?? "")
                    : selectedBill?.userName) ?? ""}
                </Typography>
              </div>
              <div
                className={
                  returnValue?.type === "Return on Sales" ? "w20Per" : "w25Per"
                }
              >
                <Typography className="gray29 fs12 font-medium">
                  Invoice number
                </Typography>
                <Typography className="fs14 black22 txt-semi-bold">
                  {selectedBill?.invoiceNumbers ?? ""}
                </Typography>
              </div>
              <div
                className={
                  returnValue?.type === "Return on Sales" ? "w20Per" : "w25Per"
                }
              >
                <Typography className="gray29 fs12 font-medium">
                  Invoice date
                </Typography>
                <Typography className="fs14 black22 txt-semi-bold">
                  {moment(new Date(selectedBill?.createdDate)).format(
                    "DD MMM YYYY",
                  )}
                </Typography>
              </div>
            </div>
          </div>

          <div className="flex-row-ali-cen mt20 ph10">
            <div className="w25Per">
              <Typography className="txt-semi-bold fs12 blue-color">
                Service/Item Name
              </Typography>
            </div>
            <div className="w15Per">
              <Typography className="txt-semi-bold fs12 blue-color">
                Available Qty
              </Typography>
            </div>
            <div className="w15Per">
              <Typography className="txt-semi-bold fs12 blue-color">
                Returning Qty
              </Typography>
            </div>
            <div className="w15Per">
              <Typography className="txt-semi-bold fs12 blue-color">
                Unit Price
              </Typography>
            </div>
            <div className="w15Per">
              <Typography className="txt-semi-bold fs12 blue-color">
                Refundable Taxes
              </Typography>
            </div>
            <div className="w15Per">
              <Typography className="txt-semi-bold fs12 blue-color">
                Refund Total
              </Typography>
            </div>
          </div>
          <div className="blue-hor-split mt5" />
          {returnValue?.list?.map((li, i) => {
            // const taxVal = Number(li?.tax) / Number(li?.quantity);
            const taxVal =
              Number(
                Number(li?.tax ?? 0) / Number(li?.oldReturnQuantity ?? 0),
              ) * Number(li?.returnQuantity ?? 0);

            return (
              <div className="plan-bor mt10 flex-row-ali-cen" key={`${i} li`}>
                <div className="w25Per pr10">
                  <Custom3dotString
                    className="txt-regular fs12 gray30"
                    str={li?.name}
                    length={25}
                  />
                </div>

                <div className="w15Per pr10">
                  <Typography className="txt-regular fs12 gray30">
                    {li?.quantity} Nos
                  </Typography>
                </div>
                <div className="w15Per pr10">
                  <CustomTextField
                    value={li?.returnQuantity}
                    error={!!li?.returnQuantityErr}
                    helperText={li?.returnQuantityErr}
                    fullWidth
                    endIcon
                    inputIcon="Nos"
                    handleChange={(e) =>
                      handleChangeTblValue(
                        "returnQuantity",
                        e?.target?.value,
                        i,
                      )
                    }
                  />
                </div>
                <div className="w15Per pr10">
                  <Typography className="txt-regular fs12 gray30">
                    Rs {Number(li?.mrpPrice)?.toFixed(2)}{" "}
                  </Typography>
                </div>
                <div className="w15Per pr10">
                  <Typography className="txt-regular fs12 gray30">
                    Rs {Number(taxVal) > 0 ? Number(taxVal)?.toFixed(2) : 0}
                  </Typography>
                </div>
                <div className="w15Per">
                  <Typography className="txt-regular fs12 gray30">
                    Rs{" "}
                    {Number(
                      Number(li?.returnQuantity ?? 0) *
                        Number(li?.mrpPrice ?? 0),
                    )?.toFixed(2)}{" "}
                  </Typography>
                </div>
              </div>
            );
          })}
          {listNotValid ? (
            <FormHelperText error className="flex-center mt10">
              Enter quantity for atleast one item
            </FormHelperText>
          ) : null}
          <div className="flex-row mt30">
            <div className="w30Per">
              <div className="flex-column">
                <div>
                  <CustomSelect
                    list={returnReasons}
                    value={returnValue?.returnReason}
                    handleChange={(e, value) =>
                      handleChange("returnReason", value)
                    }
                    search
                    fullWidth
                    label="Return Reason"
                    name="returnReason"
                  />
                  <div className="mt10">
                    <CustomSelect
                      list={getPersonInChargeList()}
                      value={returnValue?.personInCharge ?? null}
                      handleChange={(e, value) => {
                        handleChange("personInCharge", value);
                      }}
                      search
                      fullWidth
                      label="Person In charge"
                      name="personInCharge"
                    />
                  </div>

                  <div className="mt10">
                    <PaymentModeDropdown
                      list={RefundTypes}
                      value={returnValue?.refundMethod}
                      onChange={(e, value) => handleChange("refundMethod", e)}
                      name="refundMethod"
                      label="Refund Method"
                    />
                  </div>
                </div>
                {/* ) : null} */}
                <div className="mt10">
                  <CustomTextField
                    value={returnValue?.remarks}
                    handleChange={(e) =>
                      handleChange("remarks", e?.target?.value)
                    }
                    fullWidth
                    label="Remarks"
                    labelTop
                    name="remarks"
                    multiline
                  />
                </div>
              </div>
            </div>
            <div className="flex1-end">
              <div className="w50Per">
                <div className="flex-column">
                  <div className="flex-row-ali-cen">
                    <div className="w60Per">
                      <Typography className="black23 font-medium fs14">
                        Refund Subtotal
                      </Typography>
                    </div>
                    <div className="w40Per">
                      <Typography className="black23 font-medium fs14">
                        Rs.{" -"}
                        {Number(
                          returnValue?.type === "Return on Purchase"
                            ? getRefundValues(returnValue?.list)?.subTotal -
                                getRefundValues(returnValue?.list)?.tax
                            : // activeTab === "Purchase"
                              //   ? getSupplierRefundValues(
                              //       returnValue?.list,
                              //       clinicStocks,
                              //     )?.subTotal
                              //   :
                              getRefundValues(returnValue?.list)?.subTotal -
                                getRefundValues(returnValue?.list)?.tax,
                        )?.toFixed(2)}
                      </Typography>
                    </div>
                  </div>
                  <div className="flex-row-ali-cen mt10">
                    <div className="w60Per">
                      <Typography className="black23 font-medium fs14">
                        Refundable Taxes
                      </Typography>
                    </div>
                    <div className="w40Per">
                      <Typography className="black23 font-medium fs14">
                        Rs.{" -"}
                        {Number(
                          // activeTab === "Purchase"
                          //   ? getSupplierRefundValues(
                          //       returnValue?.list,
                          //       clinicStocks,
                          //     )?.taxes
                          //   :
                          getRefundValues(returnValue?.list)?.tax,
                        ) > 0
                          ? Number(
                              // activeTab === "Purchase"
                              //   ? getSupplierRefundValues(
                              //       returnValue?.list,
                              //       clinicStocks,
                              //     )?.taxes
                              //   :
                              getRefundValues(returnValue?.list)?.tax,
                            )?.toFixed(2)
                          : 0}
                      </Typography>
                    </div>
                  </div>

                  <div className="flex-row-ali-cen ref-ttl mt30">
                    <div className="w60Per">
                      <Typography className="blue-color txt-semi-bold fs14">
                        Refund Total
                      </Typography>
                    </div>
                    <div className="w40Per">
                      <Typography className="blue-color font-bold fs14">
                        Rs.{" -"}
                        {Number(
                          returnValue?.type === "Return on Purchase"
                            ? getRefundValues(returnValue?.list)?.subTotal
                            : // activeTab === "Purchase"
                              //   ? getSupplierRefundValues(
                              //       returnValue?.list,
                              //       clinicStocks,
                              //     )?.total
                              //   :
                              getRefundValues(returnValue?.list)?.subTotal,
                        )?.toFixed(2)}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex1-end mt20">
            <div>
              <CustomButton
                text="Save As Draft"
                smallBtn
                onClick={() => submitReturn("draft")}
              />
            </div>
            <div className="ml10">
              <CustomButton
                text="Submit"
                smallBtn
                onClick={() => submitReturn("credit")}
              />
            </div>
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default StockAdjustment;
